import React, { useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useGetPageContentQuery } from "../api/businessApi";

const About = () => {
  const { data: pageData } = useGetPageContentQuery("about");

  const getSection = useCallback((key, fallback) => {
    const section = pageData?.sections?.find((s) => s.section_key === key);
    return section?.content || fallback;
  }, [pageData]);

  const headerContent = useMemo(() => getSection("header", {
    heading: "About Us \u2013 Al-Tijarat al-Raabehah, Chicago",
    subtitle: "Empowering Mumineen for Ethical and Sustainable Business Growth",
  }), [getSection]);

  const introContent = useMemo(() => getSection("introduction", {
    body: "At Al-Tijarat al-Raabehah \u2013 Chicago, we believe that modern business challenges require modern solutions. Inspired by the guidance of Syedna Aali Qadr Mufaddal Saifuddin TUS, we are committed to helping Mumineen entrepreneurs and business owners thrive by combining contemporary business practices with the ethical principles of Shariyat Mohammediyah.",
  }), [getSection]);

  const missionContent = useMemo(() => getSection("mission", {
    heading: "Our Mission",
    body: "To empower the Chicago Mumineen community by offering business education, expert advisory, and collaborative opportunities. We help entrepreneurs unlock their full potential, innovate responsibly, and make meaningful contributions to our community and the global economy.",
  }), [getSection]);

  const whatWeDoContent = useMemo(() => getSection("what_we_do", {
    heading: "What We Do",
    items: [
      { title: "Business Advisory & Mentorship:", description: "Practical guidance and mentorship to navigate challenges and unlock growth." },
      { title: "Networking & Events:", description: "Monthly events like Biz N Chai, business expos, and pitch competitions to build community and spark ideas." },
      { title: "Education & Resources:", description: "Online webinars and workshops to enhance business acumen and skills." },
      { title: "Business Directory:", description: "A growing platform to highlight and connect local businesses." },
      { title: "Support for Women & Startups:", description: "Dedicated programs for women entrepreneurs and startup founders." },
    ],
  }), [getSection]);

  const valuesContent = useMemo(() => getSection("values", {
    heading: "Our Values",
    items: [
      "Promote ethical and Shariat-compliant business practices.",
      "Foster inclusive growth and diversity in business.",
      "Build data-driven strategies for impactful outcomes.",
      "Support community partnerships and responsible scaling.",
    ],
  }), [getSection]);

  const teamContent = useMemo(() => getSection("team", {
    heading: "TIJARAT TEAM",
    members: [
      "Mulla Moiz Bhai Diwan",
      "Shabbir Bhai Shams",
      "Shk Shabbir Bhai Karimi",
      "Mariam Bhen Ezzy",
      "Mulla Mustafa bhai Aladin",
      "Mariyam Ben Gulamhusein",
      "Mansoor Bhai Anjarwala",
    ],
  }), [getSection]);

  const ctaContent = useMemo(() => getSection("cta", {
    heading: "Get Involved",
    body: "Join us at our upcoming events, enroll in advisory sessions, or list your business in our directory. We are here to help you grow.",
    button_text: "Contact Us",
    button_link: "/contact",
  }), [getSection]);

  return (
    <div className="min-h-screen pt-24 md:pt-12 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="md:text-4xl text-2xl font-bold text-[#135879] mb-4">
            {headerContent.heading}
          </h1>
          <p className="md:text-xl text-base text-[#d4af37]">
            {headerContent.subtitle}
          </p>
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-lg shadow-lg p-6 mb-8 backdrop-blur-sm">
          <p className="text-base md:text-lg leading-relaxed mb-6">
            {introContent.body}
          </p>
        </section>

        {/* Mission */}
        <section className="bg-white rounded-lg shadow-lg p-6 mb-8 backdrop-blur-sm">
          <h2 className="md:text-2xl text-xl font-semibold text-[#135879] mb-4">
            {missionContent.heading}
          </h2>
          <p className="text-base md:text-lg leading-relaxed">
            {missionContent.body}
          </p>
        </section>

        {/* What We Do */}
        <section className="bg-white rounded-lg shadow-lg p-6 mb-8 backdrop-blur-sm">
          <h2 className="md:text-2xl text-xl font-semibold text-[#135879] mb-4">
            {whatWeDoContent.heading}
          </h2>
          <ul className="space-y-4">
            {(whatWeDoContent.items || []).map((item, index) => (
              <li key={index} className="flex md:text-base text-sm items-start">
                <div className="flex-shrink-0 h-6 w-6 text-[#d4af37]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
                <span className="ml-3 ">
                  <span className="font-semibold text-[#135879]">
                    {item.title}
                  </span>{" "}
                  {item.description}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* Values */}
        <section className="bg-white rounded-lg shadow-lg p-6 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-semibold text-[#135879] mb-4">
            {valuesContent.heading}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(valuesContent.items || []).map((item, index) => (
              <li key={index} className="flex md:text-base text-sm items-start">
                <div className="flex-shrink-0 h-6 w-6 text-[#d4af37]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span className="ml-3 ">
                  {typeof item === "string" ? item : item.text}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* TIJARAT TEAM */}
        <section className="bg-white rounded-lg shadow-lg p-6 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-semibold text-[#135879] mb-6 text-center">
            {teamContent.heading}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(teamContent.members || []).map((member, index) => {
              const memberName = typeof member === "string" ? member : member.name;
              return (
              <div
                key={index}
                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#135879] text-white flex items-center justify-center font-medium">
                  {memberName.split(" ")[0]?.[0] || ""}
                  {memberName.split(" ")[1]?.[0] || ""}
                </div>
                <span className="ml-3 text-gray-800">{memberName}</span>
              </div>
            );
            })}
          </div>
        </section>

        {/* Call to Action */}
        <section className="bg-[#135879] rounded-lg shadow-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">{ctaContent.heading}</h2>
          <p className="mb-6 text-gray-100">
            {ctaContent.body}
          </p>
          <Link
            to={ctaContent.button_link || "/contact"}
            className="bg-[#d4af37] hover:bg-[#e6c04f] text-[#135879] font-semibold py-2 px-6 rounded-md transition duration-300 inline-block"
          >
            {ctaContent.button_text}
          </Link>
        </section>
      </div>
    </div>
  );
};

export default About;
